/**
 * Text matching for every search box and picker in ATLAS.
 *
 * MUI's default Autocomplete filter — and the `.toLowerCase().includes(q)` that
 * grew up beside it in a dozen list pages — is a plain substring test that keeps
 * the source array's order. Across ~1,570 products that means typing a fixture's
 * exact name returns every product merely CONTAINING that string, in database
 * order, with the exact match buried. #409 and #707 fixed that for two pickers;
 * this is the same matcher, in the one place backend, `@atlas/ui` and the app
 * can all reach it.
 *
 * Two problems, two answers:
 *
 * - **Ordering** is fixed by ranking (`rankMatch`), for lists where the result
 *   order is ours to choose — a picker's popup.
 * - **Near-misses** are fixed by normalising. Product names are punctuated
 *   inconsistently ("SHOCKERBARFXIP - B", "COLORado Expo W", "Ovation Rêve E1"),
 *   so comparing on letters and digits alone lets "shocker bar fx ip" find
 *   "SHOCKERBARFXIP" and "reve e1" find "Rêve E1".
 *
 * A sortable table gets the second and not the first: re-ordering rows by match
 * quality would silently override the column sort the reader chose.
 */
/** Strip accents, case, and punctuation so only letters and digits remain. */
export function normalizeForSearch(value) {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}
/**
 * The query split into terms that must ALL match, each normalised.
 *
 * Splitting before normalising is what keeps the words apart: normalising first
 * would glue "strike array" into one token and stop it matching a row that says
 * "STRIKE 1" in one column and "Array" in another.
 */
export function searchTerms(query) {
    return query
        .split(/\s+/)
        .map(normalizeForSearch)
        .filter(Boolean);
}
/**
 * Does `text` satisfy every term in `query`?
 *
 * Terms are ANDed, so "maverick storm" narrows rather than widens. An empty or
 * punctuation-only query matches everything, which is what an empty search box
 * should do.
 */
export function matchesSearch(text, query) {
    const terms = searchTerms(query);
    if (terms.length === 0)
        return true;
    const haystack = normalizeForSearch(text);
    return terms.every((term) => haystack.includes(term));
}
/**
 * How well `label` answers `query`. Lower sorts first; -1 is no match.
 *
 * The literal-substring tier sits above the normalised one so that what the
 * reader actually typed, punctuation and all, beats a match that only appears
 * once the punctuation is thrown away.
 */
export function rankMatch(label, query) {
    const nLabel = normalizeForSearch(label);
    const nQuery = normalizeForSearch(query);
    if (!nQuery)
        return 0;
    if (nLabel === nQuery)
        return 0; // exact
    if (nLabel.startsWith(nQuery))
        return 1; // prefix
    if (label.toLowerCase().includes(query.trim().toLowerCase()))
        return 2; // literal substring
    if (nLabel.includes(nQuery))
        return 3; // substring ignoring punctuation
    if (matchesSearch(label, query))
        return 4; // all terms present, in any order
    return -1;
}
/**
 * Filter and rank options for a picker.
 *
 * Capped because rendering 1,500+ options is slow and nobody scrolls that far —
 * with exact and prefix matches sorted to the top, anything past the cap is
 * noise. Shorter labels win ties, so "STRIKE 1" beats "STRIKE 1 Array Mounting
 * Bracket" when you typed "strike 1".
 */
export function rankOptions(options, query, getLabel, limit = 50) {
    const trimmed = query.trim();
    if (!trimmed)
        return options.slice(0, limit);
    const scored = [];
    for (const option of options) {
        const label = getLabel(option);
        const score = rankMatch(label, trimmed);
        if (score >= 0)
            scored.push({ option, score, label });
    }
    scored.sort((a, b) => a.score - b.score || a.label.length - b.label.length || a.label.localeCompare(b.label));
    return scored.slice(0, limit).map((s) => s.option);
}
//# sourceMappingURL=search.js.map